import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../api/auth-context';
import { DEFAULT_WORKSPACE_ID, resolveApiOrigin, resolveUseMock } from '../api/config';
import { BrandMark } from './BrandMark';

/**
 * Branded sign-in (final-look direction §4). Primary path is Google OIDC (§7.2):
 * the button redirects to `GET /auth/google/start`, which completes back through
 * `/auth/callback`. The dev-token form is collapsed under "Development token"
 * for local DEV-auth mode; production builds never render it, so nobody can be
 * talked into pasting someone else's token into the live site.
 */
const SHOW_DEV_TOKEN = import.meta.env.DEV || resolveUseMock(import.meta.env.VITE_USE_MOCK);

export function Login(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const apiOrigin = resolveApiOrigin(import.meta.env.VITE_API_ORIGIN);

  if (auth.isAuthenticated) return <Navigate to="/" replace />;

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    const trimmed = token.trim();
    if (trimmed === '') return;
    setSubmitted(true);
    auth.login(trimmed, 0, DEFAULT_WORKSPACE_ID);
    navigate('/', { replace: true });
  }

  return (
    <main className="login-page">
      <div className="login-card">
        <BrandMark size={48} />
        <p className="studio-eyebrow">ItsAllPrep</p>
        <h1>Welcome back</h1>
        <p className="muted">Your next study session starts here.</p>
        <button
          type="button"
          className="login-google"
          onClick={() => {
            window.location.assign(`${apiOrigin}/auth/google/start`);
          }}
        >
          Sign in with Google
        </button>

        {SHOW_DEV_TOKEN && (
          <details className="dev-token">
            <summary>Development token</summary>
            <form onSubmit={onSubmit} aria-label="Sign in">
              <div className="field">
                <label htmlFor="dev-token">Development token</label>
                <input
                  id="dev-token"
                  type="password"
                  autoComplete="off"
                  value={token}
                  onChange={(event) => setToken(event.target.value)}
                  placeholder="paste the DEV token"
                />
                <p className="hint">Sent as `Authorization: Bearer …` on every request.</p>
              </div>
              <button type="submit" disabled={submitted || token.trim() === ''}>
                Sign in
              </button>
            </form>
          </details>
        )}

        <p className="muted login-origin">API origin: {apiOrigin}</p>
      </div>
    </main>
  );
}
